<template>
  <v-dialog
    v-model="show"
    max-width="600"
    overlay-color="darktext"
    overlay-opacity="0.6"
    :persistent="loading ? true : false"
    disabled
    scrollable
  >
    <v-card class="pa-5">
      <AlertMessages
        v-if="alertValue"
        :alert-type="alertType"
        :alert-msg="alertMsg"
        @reset="alertValue = false"
      />
      <v-card-title class="justify-center">
        {{ cdTitle }}
      </v-card-title>
      <v-card-subtitle class="text-center pt-5">
        {{ cdText }}
      </v-card-subtitle>
      <v-card-text>
        <v-form
          ref="form"
          v-model="valid"
          @keyup.enter="submitDialogBtn()"
          @submit.prevent
        >
          <v-text-field
            v-model="title"
            data-testid="dialog-input"
            solo
            elevation="6"
            class="rounded-xxl text-body-2 pt-4"
            :rules="titleRules"
            clearable
            :placeholder="$t('createTemplateTitlePlaceholder')"
          />

          <v-textarea
            v-model="description"
            data-testid="template-description"
            elevation="6"
            class="rounded-xxl text-body-2"
            auto-grow
            solo
            :placeholder="$t('createTemplateDescriptionPlaceholder')"
            :rules="descriptionRules"
          /> 

          <v-hover>
            <template v-slot:default="{ hover }">
              <v-card
                data-testid="template-cover-card"
                rounded="md"
                class="mx-auto pa-0 rounded-xxl mb-5"
                :elevation="hover ? 8 : 5"
                max-width="400"
              >
                <v-card-actions class="pa-0 ">
                  <v-img
                    class="rounded-xxl"
                    max-height="280"
                    :src="coverURL"
                    data-testid="template-image"
                  />
                  <v-file-input 
                    ref="file_input_cover"
                    v-model="coverImageData"
                    accept="image/png, image/jpeg"
                    data-testid="cover-file-input"
                    class="d-none"
                    @change="uploadCover"
                  />
                  <div class="center-button-on-image">
                    <v-btn
                      :loading="loadingCover"
                      data-testid="upload-cover-button"
                      mx-auto
                      color="accent"
                      class="rounded-xxl text-none px-10"
                      elevation="2"
                      relative
                      bottom
                      left
                      @click="showUploadCover"
                    >
                      {{ $t('editImage') }}
                    </v-btn>
                  </div>
                </v-card-actions>
              </v-card>
            </template>
          </v-hover>
          <div
            v-if="mixedContent"
            class="colored-options mb-5"
          >
            <v-checkbox
              v-model="includeDraft"
              color="accent"
              class="black--text pa-5 option rounded-xxl"
              :class="{ 'selected' : includeDraft}"
              data-testid="include-draft"
              hide-details
            >
              <template v-slot:label>
                <div>
                  <span class="text-subtitle-1 font-weight-bold">{{ $t('createTemplateMixedContentAsDraftTitle') }}</span><br> 
                  <span class="text-subtitle-2 grey--text font-weight-regular">{{ $t('createTemplateMixedContentAsDraftText') }}</span> 
                </div>
              </template>
            </v-checkbox>
          </div>
        </v-form>         
      </v-card-text>
      <v-card-actions>
        <v-spacer />
        <v-btn
          ref="cancel"
          data-testid="cancel-button"
          color="darktext"
          min-width="100"
          dark
          elevation="0"
          class="rounded-xxl text-none px-4 cancel"
          :disabled="loading"
          @click="show=false"
        >
          {{ cdCancelBtn }}
        </v-btn>
        <v-btn
          ref="submit"
          data-testid="submit-button"
          type="submit"
          color="accent"
          min-width="100"
          dark
          elevation="0"
          class="rounded-xxl text-none px-4 submit create-template"
          :loading="loading"
          :disabled="loadingCover || loading"
          @click="submitDialogBtn()"
        >
          {{ cdSubmitBtn }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>
<script>
import { ruleRequired, ruleValidMin2Max70Characters, ruleValidMin2Characters } from '../rules/formRules.js';
import i18n from '../plugins/i18n';
import { getActivityPages, uploadCoverImage, createTemplate} from '../api/activityApi'
import AlertMessages  from './AlertMessages'

export default {
  components: {
        AlertMessages
    },
  props: {
    visible: Boolean,
    cdTitle: {
      type: String,
      default: ''
    },
    cdText: {
      type: String,
      default: ''
    },
    cdSubmitBtn: {
      type: String,
      default: i18n.t('submit')
    },
    cdCancelBtn: {
      type: String,
      default: i18n.t('cancel')
    },
    id: {
      type: String,
      default: ''
    }
  },
  data(){
      return{
          spaceId: window.$cookies.get('space_id'),
          valid: true,
          titleRules: ruleRequired('labelTitle').concat(ruleValidMin2Max70Characters('labelTitle')),
          descriptionRules: ruleRequired('labelDescription').concat(ruleValidMin2Characters('labelDescription')),
          alertMsg:'',
          alertType:'',
          alertValue: false,
          title: '',
          description: '',
          coverImageData: null,
          coverURL: require('../assets/background-hub.jpg'),
          coverPath: null,
          includeDraft: false,
          mixedContent: false,
          loadingCover: false,
          loading: false
      }
    },    
    computed: {
        show: {
            get () {
                return this.visible
            },
            set (value) {
                if (!value) {
                    this.$emit('close')
                }
            }
        }
    },
    async created(){
        await this.getActivityPages()
    },

    methods: {
        async getActivityPages(){
           try {
            const response = await getActivityPages(this.id)
            if (response.status == 200) {
                if(response.data.data.filter(page => page.attributes.is_published == true && page.attributes.has_draft == true).length > 0){
                    this.mixedContent = true
                }
            }
          } catch (e) {
            if(e.response){
                this.alertType = 'error'
                this.alertMsg =  i18n.t('serverError')
                this.$emit('proceedSubmit', this.alertMsg, this.alertType)
            }
          }
        },
        async submitDialogBtn() {
            this.valid = this.$refs.form.validate()
            if(this.valid){
                this.loading = true
                await this.createTemplate()
                this.loading = false
            }
        },
        async uploadCover(){
            if(this.coverImageData.size > 5*1024*1024){
                this.alertValue = true
                this.alertType = 'error'
                this.alertMsg =  i18n.t('uploadCoverError')
            }else {
                this.loadingCover = true
                try {
                    let data = new FormData();
                    data.append('file', this.coverImageData);
                    data.append('type', 'cover');
                    data.append('space_id', this.spaceId);

                    const response = await uploadCoverImage(data) 
                    if(response.status == 200) {
                        this.loadingCover = false
                        this.alertMsg = ''
                        this.coverURL = URL.createObjectURL(this.coverImageData)
                        this.coverPath = response.data.meta.detail
                    }
                } catch (e) {
                    if(e.response){
                        this.alertValue = true
                        this.alertType = 'error'
                        if (e.response.status == 422 ){
                            for(let i = 0; i < e.response.data.errors.length; i++){
                                if(e.response.data.errors[i].meta[0].code == 'extensionUnsupported' || e.response.data.errors[i].meta[0].code == 'fileOversize'){
                                    this.alertMsg =  i18n.t('uploadCoverError')
                                }else {
                                    this.alertMsg = i18n.t('serverError')
                                }
                            }
                        } else if (e.response.status == 403){
                            for(let i = 0; i < e.response.data.errors.length; i++){
                                if(e.response.data.errors[i].detail == 'Storage is full'){
                                    this.alertMsg =  i18n.t('storageFullError')
                                }
                                else {
                                    this.$router.push({name: 'PageAccessDenied'})
                                }
                            }
                            
                        } else {
                            this.alertMsg =  i18n.t('serverError')       
                        }                     
                    }else{
                        this.alertValue = true
                        this.alertType = 'error'
                        this.alertMsg =  i18n.t('serverError')
                    }
                    this.loadingCover = false
                }
            }
        },
        
        showUploadCover(){
            this.$refs.file_input_cover.$refs.input.click()
        },

        async createTemplate(){
            try {
                let data = {
                    data: {
                        type: "activities",
                        id: this.id, 
                        attributes: {
                            title: this.title.trim(),
                            description: this.description,
                            image: this.coverPath,
                            use_draft: this.includeDraft
                        },
                        relationships: {
                            space: {
                                data: {
                                    type: "spaces",
                                    id: "" + this.spaceId + ""
                                }
                            }
                        }
                    }
                };
                const response = await createTemplate(this.id, data);
                if(response){
                    if(response.status == 201) {                      
                      this.alertType = 'success'
                      this.alertMsg =  i18n.t('createTemplateSuccess')
                      this.$emit('proceedSubmit', this.alertMsg, this.alertType)     
                    }
                }
            } catch (e) {
                if(e.response){
                    this.alertType = 'error'
                    if (e.response.status == 422 ){
                        this.alertMsg =  i18n.t('serverError')
                    } else if (e.response.status == 403){
                        this.$router.push({name: 'PageAccessDenied'})
                    } else {
                        this.alertMsg =  i18n.t('serverError')
                    }
                }else{
                    this.alertType = 'error'
                    this.alertMsg =  i18n.t('serverError')     
                }
                this.$emit('proceedSubmit', this.alertMsg, this.alertType)
            }
        }
    },
}
</script>